import React from 'react'
import {
  Box,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core'
import { Trash } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { HourSeriesFragment, HourSeriesType } from '../../generated/graphql'
import { createSortByProperty, groupBy, sum } from '../../util/array-utility'
import {
  energyPerAreaUnit,
  energyPerYearUnit,
  formatEnergy,
  formatEnergyPerArea,
  formatPower,
  formatPowerPerArea,
  powerPerAreaUnit,
  powerUnit,
} from '../../util/formatters'
import { useAlert } from '../alert/AlertProvider'
import { Icon } from '../common/Icon'

const useStyles = makeStyles((theme) => ({
  nested: {
    paddingLeft: theme.spacing(8),
  },
}))

type Column = {
  title: string
  value: (values: number[]) => number | string
}

type ColumnGroups = Record<string, Column[]>

type Props = {
  hourSeries: HourSeriesFragment[]
  size?: 'small' | 'medium'
  onDeleteHourSeries?: (hourSeries: HourSeriesFragment) => Promise<void>
}

export function HourSeriesList({
  hourSeries,
  size,
  onDeleteHourSeries,
}: Props): React.ReactElement {
  const classes = useStyles()
  const { t } = useTranslation()
  const { alert } = useAlert()

  const hourSeriesByType = groupBy(
    hourSeries,
    (singleHourSeries) => singleHourSeries.type,
  )

  const powerColumns = [
    {
      title: energyPerYearUnit,
      value: (values: number[]) =>
        formatEnergy(sum(values), { omitUnit: true }),
    },
    {
      title: powerUnit,
      value: (values: number[]) =>
        formatPower(Math.max(...values), { omitUnit: true }),
    },
  ]

  const powerDensityColumns = [
    {
      title: energyPerAreaUnit,
      value: (values: number[]) =>
        formatEnergyPerArea(sum(values), 1, { omitUnit: true }),
    },
    {
      title: powerPerAreaUnit,
      value: (values: number[]) =>
        formatPowerPerArea(Math.max(...values), 1, { omitUnit: true }),
    },
  ]

  const columnGroups: ColumnGroups = {
    [HourSeriesType.HeatingDemand]: powerColumns,
    [HourSeriesType.CoolingDemand]: powerColumns,
    [HourSeriesType.ElectricityDemand]: powerColumns,
    [HourSeriesType.HeatingDemandDensity]: powerDensityColumns,
    [HourSeriesType.CoolingDemandDensity]: powerDensityColumns,
    [HourSeriesType.ElectricityDemandDensity]: powerDensityColumns,
    [HourSeriesType.OutdoorTemperature]: [
      {
        title: t('projectHourSeries.columns.min'),
        value: (values: number[]) => Math.min(...values),
      },
      {
        title: t('projectHourSeries.columns.max'),
        value: (values: number[]) => Math.max(...values),
      },
      {
        title: t('projectHourSeries.columns.average'),
        value: (values: number[]) => Math.round(sum(values) / values.length),
      },
    ],
    [HourSeriesType.SolarPanelRatedPowerDensity]: powerDensityColumns,
    [HourSeriesType.WasteHeating]: powerColumns,
    [HourSeriesType.Other]: [],
    [HourSeriesType.MeasuredOutdoorTemperature]: [
      {
        title: t('projectHourSeries.columns.min'),
        value: (values: number[]) => Math.min(...values),
      },
      {
        title: t('projectHourSeries.columns.max'),
        value: (values: number[]) => Math.max(...values),
      },
      {
        title: t('projectHourSeries.columns.average'),
        value: (values: number[]) => Math.round(sum(values) / values.length),
      },
    ],
    [HourSeriesType.MeasuredHeatingDemand]: powerColumns,
    [HourSeriesType.MeasuredCoolingDemand]: powerColumns,
    [HourSeriesType.MeasuredElectricityDemand]: powerColumns,
  }

  return (
    <Box width="100%">
      {Object.entries(hourSeriesByType).map(([type, hourSeriesList]) => (
        <TableContainer key={type} data-cy={`hour-series-table-${type}`}>
          <Table size={size}>
            <TableHead>
              <TableRow>
                <TableCell width="60%">{t(`hourSeriesType.${type}`)}</TableCell>
                {columnGroups[type].map((column) => (
                  <TableCell key={column.title}>{column.title}</TableCell>
                ))}
                {onDeleteHourSeries && <TableCell />}
              </TableRow>
            </TableHead>

            <TableBody>
              {hourSeriesList
                .sort(createSortByProperty('name'))
                .map((singleHourSeries) => (
                  <TableRow key={singleHourSeries.id}>
                    <TableCell className={classes.nested}>
                      {singleHourSeries.name}
                    </TableCell>
                    {columnGroups[type].map((column) => (
                      <TableCell key={column.title}>
                        {column.value(singleHourSeries.values)}
                      </TableCell>
                    ))}
                    {onDeleteHourSeries && (
                      <TableCell align="right">
                        {!singleHourSeries.isInUse && (
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation()
                              alert(
                                t(
                                  'projectHourSeries.deleteHourSeriesDialog.title',
                                ),
                                t(
                                  'projectHourSeries.deleteHourSeriesDialog.message',
                                ),
                                [
                                  { title: t('common.cancel') },
                                  {
                                    title: t(
                                      'projectHourSeries.deleteHourSeriesDialog.confirm',
                                    ),
                                    color: 'secondary',
                                    onPress: async () => {
                                      await onDeleteHourSeries(singleHourSeries)
                                    },
                                  },
                                ],
                              )
                            }}
                          >
                            <Icon component={Trash} size="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      ))}
    </Box>
  )
}
